import { useState, type CSSProperties } from "react";
import AppleLogo from "@/components/icons/AppleLogo";
import DownloadCta from "@/components/desktop/DownloadCta";
import WindowsWaitlistButton from "@/components/desktop/WindowsWaitlistButton";
import WebglRive from "@/components/ui/WebglRive";

// The new hero (nav + headline + animated MacBook), shared by /herotest and
// the home page. Render it inside a .herotest element and start ./engine on
// that element; the engine fills in the notch, windows and dock.

const A = "/herotest-assets";
// vibing Vovy (looping Rive): in the headline, and the one who falls into the notch
const VIBING = "/animations/vibingvovy.riv";

// Vovy, sized to the headline type; each loop he falls from here into the
// MacBook's notch, then fades back in (engine.ts). A static Vovy sits behind
// the Rive so the headline is never blank while the .riv loads, and hides as
// soon as the animation reports ready so the two never show together.
function HeadlineVovy() {
  const [ready, setReady] = useState(false);
  return (
    <span className="h1-vovy" id="hvovy" aria-hidden="true">
      <img src={`${A}/vovy-mascot.webp`} alt="" className="vv-fb" style={ready ? { visibility: "hidden" } : undefined} />
      <WebglRive src={VIBING} className="vv-r" onReady={() => setReady(true)} />
    </span>
  );
}

export interface HeroMarkupProps {
  /** Nav links as [href, label]; they hide below 900px. */
  links: Array<[string, string]>;
  /** DownloadCta placement prefix, e.g. "home" → "home-nav", "home-hero". */
  placement: string;
  /** Where "See how it works" scrolls to. */
  howHref: string;
}

export default function HeroMarkup({ links, placement, howHref }: HeroMarkupProps) {
  return (
    <>
        <nav className="nav" id="nav" aria-label="Main">
          <a className="logo" href="#top"><img src={`${A}/vovy-lockup.webp`} alt="Vovy" /></a>
          <div className="links">{links.map(([href, label]) => <a key={href} href={href}>{label}</a>)}</div>
          <DownloadCta placement={`${placement}-nav`} className="btn pri sm" iconClassName="apple-ic" />
        </nav>

        <header className="hero" id="top" data-vovy-hero>
          {/* Vovy, who drops from the top of the hero into the notch at the start of each loop */}
          <div className="ndrop" id="ndrop" aria-hidden="true"><WebglRive src={VIBING} className="vv-r" /></div>
          <h1 className="h-in">
            {/* words rise in one by one, 50ms apart (herotest.css) */}
            <span className="w" style={{ "--i": 0 } as CSSProperties}>You</span> <span className="w" style={{ "--i": 1 } as CSSProperties}>talk.</span>
            <br />
            <span className="gline">
            <span className="w g" style={{ "--i": 2 } as CSSProperties}>Vovy</span>{" "}
            <HeadlineVovy />{" "}
            <span className="w g" style={{ "--i": 3 } as CSSProperties}>builds.</span>
            </span>
          </h1>
          <p className="sub h-in">Vovy is a computer use agent that works like a technical co-founder. <b>Say what you want and watch it build in any app on your computer.</b></p>
          <div className="cta h-in" data-vovy-hero-content><DownloadCta placement={`${placement}-hero`} className="btn pri" iconClassName="apple-ic" /><a className="btn ghost" href={howHref}>See how it works</a></div>
          {/* Windows: no build yet, so the same waitlist dialog the old hero used,
              as a quiet text link under the CTAs. */}
          <p className="h-in" style={{ marginTop: "16px", fontSize: "13px", lineHeight: 1.4, color: "var(--muted)" }}>
            <WindowsWaitlistButton
              className="inline cursor-pointer bg-transparent p-0 font-medium underline underline-offset-4 transition-colors hover:text-[var(--navy)]"
              iconClassName="hidden"
            />
          </p>
          {/* The MacBook, built in HTML so the notch, menu bar and wallpaper are real */}
          <div className="stage-track">
            <div className="stage">
              <i className="bloom" aria-hidden="true"></i>
              <div className="mac" id="hscreen" role="img" aria-label="A MacBook with Vovy in the notch">
                <div className="mac-zoom">
                  <div className="lid">
                    <div className="display">
                      {/* everything on the MacBook screen */}
                      <div className="scene" id="hscene">
                        <div className="menubar" aria-hidden="true">
                          <div className="mb-l">
                            <AppleLogo className="mb-apple" />
                            <b>Finder</b><span>File</span><span>Edit</span><span>View</span><span>Go</span><span>Window</span><span>Help</span>
                          </div>
                          <div className="mb-r">
                            <svg viewBox="0 0 28 14" className="mb-bat"><rect x=".75" y=".75" width="23" height="12.5" rx="3.6" fill="none" stroke="currentColor" strokeWidth="1.3" opacity=".55" /><rect x="2.6" y="2.6" width="19.3" height="8.8" rx="2" fill="currentColor" /><path d="M25.6 4.8v4.4c.9-.3 1.5-1.2 1.5-2.2s-.6-1.9-1.5-2.2z" fill="currentColor" opacity=".55" /></svg>
                            <svg viewBox="0 0 20 15" className="mb-ico"><path d="M10 14.2l2.6-3.1a4 4 0 00-5.2 0z" fill="currentColor" /><path d="M4.6 8.3a8.2 8.2 0 0110.8 0l1.6-1.9a10.7 10.7 0 00-14 0z" fill="currentColor" /><path d="M1.2 4.3a13.4 13.4 0 0117.6 0L20 2.9A15.4 15.4 0 000 2.9z" fill="currentColor" /></svg>
                            <svg viewBox="0 0 16 16" className="mb-ico"><circle cx="6.6" cy="6.6" r="5" fill="none" stroke="currentColor" strokeWidth="1.8" /><path d="M10.4 10.4l4.4 4.4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
                            <svg viewBox="0 0 18 16" className="mb-ico"><rect x=".9" y="1.4" width="16.2" height="5.6" rx="2.8" fill="none" stroke="currentColor" strokeWidth="1.4" /><circle cx="12.4" cy="4.2" r="1.7" fill="currentColor" /><rect x=".9" y="9" width="16.2" height="5.6" rx="2.8" fill="none" stroke="currentColor" strokeWidth="1.4" /><circle cx="5.6" cy="11.8" r="1.7" fill="currentColor" /></svg>
                            <span className="mb-clock" id="clock">Fri Sep 25&nbsp; 11:55 AM</span>
                          </div>
                        </div>
                        <div className="wp" id="n1" aria-hidden="true"></div>
                        {/* greeting that pops out of the notch at the start of each loop */}
                        <div className="nbub" id="nbub" aria-hidden="true">
                          <span className="nbub-tail"></span>
                          <img src={`${A}/vovy-listening.webp`} alt="" />
                          <span className="nbub-vp">
                            <span className="nbub-line on">Hey! I’m Vovy.</span>
                            <span className="nbub-line">Press <kbd>fn</kbd> + <kbd>ctrl</kbd> to say hi</span>
                          </span>
                        </div>
                        <div className="toast" id="t1" role="status"></div>
                        <div className="hwins" id="hwins" aria-hidden="true"></div>
                        {/* Dock: real macOS app icons, rendered from the installed apps */}
                        <div className="hdock" id="hdock" aria-hidden="true">
                          {["finder", "safari", "chrome", "chatgpt", "cursor", "claude", "figma", "slack", "notion", "messages", "terminal", "settings"].map((app) => (
                            <span key={app} data-app={app} className={app === "finder" ? "run" : undefined}><img src={`${A}/dock/${app}.png`} alt="" /></span>
                          ))}
                          <i className="hdock-sep"></i>
                          <span data-app="trash"><img src={`${A}/dock/trash.png`} alt="" /></span>
                        </div>
                        <div className="cur" id="c1" style={{ left: "30%", top: "70%" }} aria-hidden="true"></div>
                      </div>
                    </div>
                  </div>
                  <div className="base" aria-hidden="true"></div>
                </div>
              </div>
            </div>
          </div>
        </header>
    </>
  );
}
